import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { X, Volume2, MessageSquare, Mic, HelpCircle, Info, Bell, Download, User, Save, Sparkles } from "lucide-react";
import { pushSupported, enablePush, disablePush, isSubscribed } from "@/lib/push";
import { ttsSupported, getVoices, speak, stopSpeaking, OPENAI_VOICES } from "@/lib/tts";
import { AvatarPicker } from "@/components/AssistantAvatar";
import { toast } from "sonner";

export const SettingsDrawer = ({ open, onOpenChange, settings, onChange }) => {
    const [s, setS] = useState(settings || {});
    const [pushOn, setPushOn] = useState(false);
    const [pushBusy, setPushBusy] = useState(false);
    const [voices, setVoices] = useState([]);
    const [nameDraft, setNameDraft] = useState("");
    const [assistantNameDraft, setAssistantNameDraft] = useState("");

    useEffect(() => {
        setS(settings || {});
        setNameDraft(settings?.user_name || "");
        setAssistantNameDraft(settings?.assistant_name || "");
    }, [settings]);

    useEffect(() => {
        isSubscribed().then(setPushOn);
        if (ttsSupported()) getVoices().then(setVoices);
    }, [open]);

    const togglePush = async (want) => {
        setPushBusy(true);
        try {
            if (want) {
                await enablePush();
                setPushOn(true);
                toast.success("Notifications enabled");
            } else {
                await disablePush();
                setPushOn(false);
                toast.success("Notifications off");
            }
        } catch (e) {
            toast.error(e.message || "Failed");
        } finally {
            setPushBusy(false);
        }
    };

    const update = (patch) => {
        const next = { ...s, ...patch };
        setS(next);
        onChange?.(patch);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="bg-meye-card border border-meye-border max-w-md text-zinc-100 p-0 gap-0"
                data-testid="settings-drawer"
            >
                <DialogTitle className="sr-only">Settings</DialogTitle>
                <div className="flex items-center justify-between px-4 py-3 border-b border-meye-border">
                    <span className="font-brand uppercase tracking-widest text-sm text-meye-cyan">settings</span>
                    <button onClick={() => onOpenChange(false)} className="text-zinc-500 hover:text-white" data-testid="settings-close">
                        <X size={16} />
                    </button>
                </div>

                <div className="p-4 space-y-6">
                    {/* Identity — name the trainer uses to greet you */}
                    <section>
                        <h4 className="text-[10px] uppercase tracking-widest text-meye-cyan font-mono mb-3 flex items-center gap-1.5">
                            <User size={11} /> your name
                        </h4>
                        <div className="flex gap-2">
                            <input
                                value={nameDraft}
                                onChange={(e) => setNameDraft(e.target.value)}
                                placeholder="what should the trainer call you?"
                                className="flex-1 bg-black/40 border border-meye-border rounded p-2 text-sm text-zinc-100 font-mono focus:outline-none focus:border-meye-cyan/60"
                                data-testid="settings-name-input"
                            />
                            <button
                                type="button"
                                disabled={!nameDraft.trim() || nameDraft.trim() === (s.user_name || "").trim()}
                                onClick={() => {
                                    const clean = nameDraft.trim();
                                    if (!clean) return;
                                    update({ user_name: clean });
                                    toast.success(`got it, ${clean.toLowerCase()}`);
                                }}
                                className="bg-meye-cyan text-black rounded px-3 py-2 text-xs font-brand font-bold uppercase tracking-widest disabled:opacity-40 flex items-center gap-1.5"
                                data-testid="settings-name-save"
                            >
                                <Save size={12} /> save
                            </button>
                        </div>
                        <p className="text-[10px] text-zinc-500 font-mono mt-2">
                            The copilot addresses you by this name in greetings and replies.
                        </p>
                    </section>

                    {/* Assistant identity — 6 SVG avatars + editable assistant name */}
                    <section>
                        <h4 className="text-[10px] uppercase tracking-widest text-meye-fuchsia font-mono mb-3 flex items-center gap-1.5">
                            <Sparkles size={11} /> your assistant
                        </h4>
                        <div className="mb-3">
                            <AvatarPicker
                                value={s.assistant_avatar}
                                onChange={(id) => update({ assistant_avatar: id })}
                                size={56}
                            />
                        </div>
                        <div className="flex gap-2">
                            <input
                                value={assistantNameDraft}
                                onChange={(e) => setAssistantNameDraft(e.target.value)}
                                placeholder="give them a name..."
                                maxLength={30}
                                className="flex-1 bg-black/40 border border-meye-border rounded p-2 text-sm text-zinc-100 font-mono focus:outline-none focus:border-meye-fuchsia/60"
                                data-testid="settings-assistant-name-input"
                            />
                            <button
                                type="button"
                                disabled={!assistantNameDraft.trim() || assistantNameDraft.trim() === (s.assistant_name || "").trim()}
                                onClick={() => {
                                    const clean = assistantNameDraft.trim();
                                    if (!clean) return;
                                    update({ assistant_name: clean });
                                    toast.success(`meet ${clean.toLowerCase()}`);
                                }}
                                className="bg-meye-fuchsia text-black rounded px-3 py-2 text-xs font-brand font-bold uppercase tracking-widest disabled:opacity-40 flex items-center gap-1.5"
                                data-testid="settings-assistant-name-save"
                            >
                                <Save size={12} /> save
                            </button>
                        </div>
                        <p className="text-[10px] text-zinc-500 font-mono mt-2">
                            Pick an avatar and give your chat assistant a name — shown at the top of Let&apos;s Chat.
                        </p>
                    </section>

                    {/* Push notifications */}
                    <section>
                        <h4 className="text-[10px] uppercase tracking-widest text-meye-fuchsia font-mono mb-3 flex items-center gap-1.5">
                            <Bell size={11} /> nagging notifications
                        </h4>
                        {pushSupported() ? (
                            <>
                                <label className="flex items-center justify-between text-sm mb-2">
                                    <span>Nag me on this device</span>
                                    <input
                                        type="checkbox"
                                        checked={pushOn}
                                        disabled={pushBusy}
                                        onChange={(e) => togglePush(e.target.checked)}
                                        className="accent-meye-fuchsia w-4 h-4"
                                        data-testid="settings-push-toggle"
                                    />
                                </label>
                                <p className="text-[10px] text-zinc-500 font-mono">
                                    When a task is due, you'll get a push every minute. Tap it to snooze (5m · 15m · 30m · 1h · 3h · tomorrow · next week · custom) or mark done.
                                </p>
                            </>
                        ) : (
                            <p className="text-xs text-zinc-500">
                                Push not supported. On iPhone: Safari → share sheet → "Add to Home Screen", then open the installed app and enable here.
                            </p>
                        )}
                        <p className="text-[10px] text-meye-cyan font-mono mt-3 flex items-center gap-1.5">
                            <Download size={11} /> Not installed yet?
                        </p>
                        <p className="text-[11px] text-zinc-400 mt-1">
                            iPhone: Safari share icon → "Add to Home Screen". Android: Chrome menu → "Install app".
                        </p>
                    </section>

                    {/* Let's Chat preferences */}
                    <section>
                        <h4 className="text-[10px] uppercase tracking-widest text-meye-fuchsia font-mono mb-3 flex items-center gap-1.5">
                            <MessageSquare size={11} /> let&apos;s chat
                        </h4>
                        <label className="flex items-center justify-between text-sm mb-3">
                            <span>Greet me when I open Let&apos;s Chat</span>
                            <input
                                type="checkbox"
                                checked={s.comms_greet_on_open !== false}
                                onChange={(e) => update({ comms_greet_on_open: e.target.checked })}
                                className="accent-meye-fuchsia w-4 h-4"
                                data-testid="settings-greet-toggle"
                            />
                        </label>

                        <div>
                            <p className="text-xs text-zinc-400 mb-2 flex items-center gap-1.5">
                                <Mic size={11} /> Voice mode
                            </p>
                            <div className="grid grid-cols-3 gap-1.5">
                                {[
                                    { id: "text", label: "Text" },
                                    { id: "ptt", label: "Push to Talk" },
                                    { id: "continuous", label: "Continuous" },
                                ].map((v) => (
                                    <button
                                        key={v.id}
                                        onClick={() => update({ comms_voice_mode: v.id })}
                                        className={`text-[11px] font-brand font-bold uppercase tracking-widest py-2 rounded border transition-colors ${
                                            (s.comms_voice_mode || "continuous") === v.id
                                                ? "border-meye-cyan text-meye-cyan bg-meye-cyan/10"
                                                : "border-meye-border text-zinc-400 hover:text-zinc-200"
                                        }`}
                                        data-testid={`settings-voice-${v.id}`}
                                    >
                                        {v.label}
                                    </button>
                                ))}
                            </div>
                            <p className="text-[10px] text-zinc-500 font-mono mt-2">
                                Continuous uses your browser's speech recognition. Chrome only. Falls back to Push to Talk.
                            </p>
                        </div>

                        <label className="flex items-center justify-between text-sm mt-4">
                            <span className="flex items-center gap-1.5">
                                <Volume2 size={13} /> Read replies aloud
                            </span>
                            <input
                                type="checkbox"
                                checked={!!s.comms_tts_enabled}
                                onChange={(e) => update({ comms_tts_enabled: e.target.checked })}
                                className="accent-meye-fuchsia w-4 h-4"
                                data-testid="settings-tts-toggle"
                            />
                        </label>
                        {ttsSupported() && s.comms_tts_enabled && (
                            <div className="mt-2 space-y-2">
                                <select
                                    value={s.comms_tts_voice || "nova"}
                                    onChange={(e) => update({ comms_tts_voice: e.target.value })}
                                    className="w-full bg-black/40 border border-meye-border rounded p-2 text-xs text-zinc-200 font-mono"
                                    data-testid="settings-tts-voice"
                                >
                                    {OPENAI_VOICES.map((v) => (
                                        <option key={v.id} value={v.id}>
                                            {v.label} — {v.description}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-[10px] text-zinc-500 font-mono">
                                    Powered by OpenAI TTS. Natural, conversational voices — streams from the trainer over the network.
                                </p>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => speak(`Hi ${s.user_name || "trainer"}, this is how I sound.`, { voiceName: s.comms_tts_voice || "nova" })}
                                        className="flex-1 text-xs bg-meye-cyan/15 border border-meye-cyan/40 text-meye-cyan rounded py-1.5"
                                        data-testid="settings-tts-preview"
                                    >
                                        Preview voice
                                    </button>
                                    <button
                                        type="button"
                                        onClick={stopSpeaking}
                                        className="text-xs bg-zinc-800 border border-meye-border text-zinc-300 rounded py-1.5 px-3"
                                    >
                                        Stop
                                    </button>
                                </div>
                            </div>
                        )}
                    </section>

                    {/* Chat commands (moved from Support tab) */}
                    <section>
                        <h4 className="text-[10px] uppercase tracking-widest text-meye-cyan font-mono mb-3 flex items-center gap-1.5">
                            <HelpCircle size={11} /> commands
                        </h4>
                        <div className="space-y-1 text-xs text-zinc-300 font-mono">
                            <div><span className="text-meye-fuchsia">import</span> — upload .json backup</div>
                            <div><span className="text-meye-fuchsia">export</span> — download .json backup</div>
                            <div><span className="text-meye-fuchsia">note: ...</span> — quick save note</div>
                            <div><span className="text-meye-fuchsia">I'm at [place]</span> — context scan</div>
                            <div><span className="text-meye-fuchsia">help</span> or <span className="text-meye-fuchsia">?</span> — commands</div>
                        </div>
                    </section>

                    {/* Gestures */}
                    <section>
                        <h4 className="text-[10px] uppercase tracking-widest text-violet-300 font-mono mb-3">gestures</h4>
                        <div className="space-y-1 text-xs text-zinc-300 font-mono">
                            <div>swipe task right → complete</div>
                            <div>swipe task left → edit</div>
                            <div>screensaver: swipe ← → to time-travel</div>
                            <div>screensaver: swipe ↑ ↓ between tasks</div>
                            <div>screensaver: double-tap a task to open it</div>
                            <div>60s idle → screensaver</div>
                        </div>
                    </section>

                    <section>
                        <h4 className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-3 flex items-center gap-1.5">
                            <Info size={11} /> about
                        </h4>
                        <p className="text-xs text-zinc-400">
                            mEYEtasks · powered by mEYEtrainer AI (Claude Fable 5). your personal knowledge graph, watched by the eye.
                        </p>
                    </section>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default SettingsDrawer;
